import { formatBetrag } from "../../format.js";

/**
 * Die Anwendertexte des Bereichs „Artikel“ (Spec Artikelmodul 0.4, „Texte“): englische Schlüssel, deutsche Werte. Hier
 * stehen Bereich, Kachel, Leer- und Fehlerzustände, Pillen und Tipps, dazu die Takt- und Laufzeitwörter EINMAL für Kachel
 * und Dialog. Die Texte des Dialogs stehen in components/artikel/articleDialogTexts.ts, die der Domäne in PRODUCT_TEXT.
 */
export const ARTICLE_TEXT = {
  area: "Artikel",
  categories: "Kategorien",
  allArticles: "Alle Artikel",
  /** Die erste Pille der Kategorien am Telefon (mit Zähler dahinter). */
  all: "Alle",
  /** Die Pseudozeile für Artikel ohne Kategorie (778); der Server liefert sie mit leerem Namen. */
  withoutCategory: "ohne Kategorie",
  editCategories: "Kategorien pflegen",
  /** Verwaltung › Artikelstamm nach 788: nur der Verweis mit Sprung. */
  caredInArea: "Artikel werden im Bereich Artikel gepflegt.",
  toArea: "Zu den Artikeln",
  newArticle: "Neuer Artikel",
  searchLabel: "Artikel durchsuchen",
  searchPlaceholder: "Name oder Artikelnummer",
  companiesLabel: "Artikel nach Unternehmen",
  stateLabel: "Zustand",
  columns: "Spalten",
  columnsLabel: "Spalten der Kachelansicht",
  /** Das Kennzeichen am stillgelegten Artikel (797, `Chip status-art="aus"`). */
  inactive: "stillgelegt",
  missingAccount: "Konto fehlt",
  /** Tipp zu „Konto fehlt“ (786): ohne Kontonummer, weil das Ersatzkonto vom Kunden abhängt. */
  missingAccountTip: "Neue Positionen mit diesem Artikel bekommen kein Konto; die Kontierung gilt dann nach "
    + "Steuerfall. Der DATEV-Export ist davon nicht betroffen.",
  noText: "Kein Text hinterlegt",
  notUsed: "noch nicht verwendet",
  noArticles: "keine Artikel",
  /** Der Kachelkopf ohne Preis (nur Altbestand, 797). */
  noPrice: "–",
  loading: "Lädt …",
  loadError: "Die Artikel konnten nicht geladen werden. Bitte die Seite neu laden.",
  tooLarge: "Zu viele Artikel für eine Ansicht. Bitte die Suche oder die Unternehmen einschränken.",
  empty: "Es sind noch keine Artikel hinterlegt.",
  categoryEmpty: "In dieser Kategorie gibt es keinen Artikel.",
  categoryGone: "Die Kategorie gibt es nicht mehr.",
  articleUnknown: "Diesen Artikel gibt es nicht oder er gehört zu einem anderen Unternehmen.",
  /** Panelknopf des Verweises an der Kachel (Stift, 788). */
  editArticle: (nummer: string): string => `Artikel ${nummer} bearbeiten`,
  /** Zugänglicher Name des „⋯“ an der Kachel. */
  moreFor: (nummer: string): string => `Weitere Aktionen zu ${nummer}`,
  more: "⋯",
  /** Zugänglicher Name einer Kategorienzeile — „anzeigen“ wie jede Zeile, die den Inhalt rechts wählt (Wortliste). */
  categoryRow: (name: string, n: number): string => `Kategorie ${name}, ${articleCount(n)}, anzeigen`,
  /** Die Pille „stillgelegte n“ — die Zahl steht immer da, auch bei 0 (die Leiste kippt nicht). */
  inactivePill: (n: number): string => `stillgelegte ${n}`,
  /** Eine Kategorie als Pille am Telefon: „Name n“. */
  categoryPill: (name: string, n: number): string => `${name} ${n}`,
} as const;

/** Die Takte am Artikel (770, 782). */
export const CYCLE_TEXT: Record<"ONCE" | "MONTHLY" | "QUARTERLY" | "YEARLY" | "ON_EFFORT", string> = {
  ONCE: "einmalig", MONTHLY: "monatlich", QUARTERLY: "vierteljährlich", YEARLY: "jährlich", ON_EFFORT: "nach Aufwand",
};

/** Die Einheiten der Vorgabe-Laufzeit (781) in Einzahl und Mehrzahl. */
export const TERM_UNIT_TEXT: Record<"MONTHS" | "YEARS", { eins: string; mehrere: string }> = {
  MONTHS: { eins: "Monat", mehrere: "Monate" },
  YEARS: { eins: "Jahr", mehrere: "Jahre" },
};

/** „n Artikel“ — das Wort hat keine Mehrzahl. */
export function articleCount(n: number): string {
  return `${n} Artikel`;
}

/** Die Einheit hinter dem Preis im Kachelkopf: „/ Monat“ (797). */
export function perUnit(unit: string): string {
  return `/ ${unit}`;
}

/** Die Preisspanne einer Kategorie: „4,50 € – 900,00 €“, eine Zahl bei gleichem Preis, ohne Preise „keine Artikel“. */
export function priceRange(min: number | null, max: number | null): string {
  if (min === null || max === null) return ARTICLE_TEXT.noArticles;
  return min === max ? formatBetrag(min, "EUR") : `${formatBetrag(min, "EUR")} – ${formatBetrag(max, "EUR")}`;
}

/** Die Verwendung an der Kachel (794): „in 7 Angeboten · 4 Aufträgen“, ein Teil mit 0 fällt weg, 0/0 „noch nicht
 *  verwendet“. */
export function usage(offers: number, orders: number): string {
  const teile = [
    ...(offers > 0 ? [`${offers} ${offers === 1 ? "Angebot" : "Angeboten"}`] : []),
    ...(orders > 0 ? [`${orders} ${orders === 1 ? "Auftrag" : "Aufträgen"}`] : []),
  ];
  return teile.length === 0 ? ARTICLE_TEXT.notUsed : `in ${teile.join(" · ")}`;
}

/** Der Gruppenkopf im Raster: „Messung & Daten · 3 Artikel“. */
export function groupHead(name: string, n: number): string {
  return `${name} · ${articleCount(n)}`;
}

/** Der Warnstatus einer Kategorienzeile: „1 ohne Konto“. */
export function missingAccount(n: number): string {
  return `${n} ohne Konto`;
}

/** Die Suche ohne Treffer: „Zu „xy“ gibt es keinen Artikel.“ */
export function searchEmpty(q: string): string {
  return `Zu „${q}“ gibt es keinen Artikel.`;
}
